import { generateId } from "../utils/helpers";
import type { PrivacyMode } from "../types/card.types";

const TEXT_ENCODER = new TextEncoder();
const TEXT_DECODER = new TextDecoder();

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

export type LrnsyEncryptedValue = {
  ciphertext: string;
  iv: string;
  salt: string;
  version: "lrnsy-webcrypto-aes-gcm-v1";
};

export type LrnsyEncryptedContentBundle = {
  privacyMode: PrivacyMode;
  encryptedMessage: LrnsyEncryptedValue;
  encryptedPersonalNote?: LrnsyEncryptedValue | null;
  encryptedMemory?: LrnsyEncryptedValue | null;
  encryptedInsideJoke?: LrnsyEncryptedValue | null;

  /**
   * Standard Private Mode can store a recoverability material.
   * Ultra Private Mode must never send the rawKey or recoverable key to DB.
   */
  encryptedKey?: string | null;
  fragmentKey?: string | null;
};

function bytesToBase64(bytes: ArrayBuffer | Uint8Array): string {
  const array = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = "";
  array.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

async function sha256(value: string): Promise<string> {
  const hash = await crypto.subtle.digest("SHA-256", TEXT_ENCODER.encode(value));
  return bytesToBase64(hash);
}

async function deriveAesKey(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    toArrayBuffer(TEXT_ENCODER.encode(secret)),
    "PBKDF2",
    false,
    ["deriveKey"]
  );

  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: toArrayBuffer(salt),
      iterations: 310_000,
      hash: "SHA-256",
    },
    baseKey,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    ["encrypt", "decrypt"]
  );
}

class LrnsyCryptoService {
  generatePrivateKey(): string {
    const bytes = crypto.getRandomValues(new Uint8Array(32));
    return bytesToBase64(bytes);
  }

  async hashPasscode(passcode: string, saltBase64?: string) {
    const salt = saltBase64
      ? base64ToBytes(saltBase64)
      : crypto.getRandomValues(new Uint8Array(16));

    const hash = await sha256(`${bytesToBase64(salt)}:${passcode}`);
    return {
      hash,
      salt: bytesToBase64(salt),
    };
  }

  async encryptText(plaintext: string, secret: string): Promise<LrnsyEncryptedValue> {
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await deriveAesKey(secret, salt);

    const ciphertext = await crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
      },
      key,
      TEXT_ENCODER.encode(plaintext || "")
    );

    return {
      ciphertext: bytesToBase64(ciphertext),
      iv: bytesToBase64(iv),
      salt: bytesToBase64(salt),
      version: "lrnsy-webcrypto-aes-gcm-v1",
    };
  }

  async decryptText(payload: LrnsyEncryptedValue, secret: string): Promise<string> {
    const salt = base64ToBytes(payload.salt);
    const iv = base64ToBytes(payload.iv);
    const key = await deriveAesKey(secret, salt);

    const plaintext = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: toArrayBuffer(iv),
      },
      key,
      toArrayBuffer(base64ToBytes(payload.ciphertext))
    );

    return TEXT_DECODER.decode(plaintext);
  }

  async encryptCardContent(params: {
    message: string;
    personalNote?: string;
    memory?: string;
    insideJoke?: string;
    privacyMode: PrivacyMode;
    senderId: string;
    cardRecoverySecret?: string;
  }): Promise<LrnsyEncryptedContentBundle> {
    const contentKey = this.generatePrivateKey();

    const encryptedMessage = await this.encryptText(params.message, contentKey);
    const encryptedPersonalNote = params.personalNote
      ? await this.encryptText(params.personalNote, contentKey)
      : null;
    const encryptedMemory = params.memory
      ? await this.encryptText(params.memory, contentKey)
      : null;
    const encryptedInsideJoke = params.insideJoke
      ? await this.encryptText(params.insideJoke, contentKey)
      : null;

    if (params.privacyMode === "ultra_private") {
      return {
        privacyMode: params.privacyMode,
        encryptedMessage,
        encryptedPersonalNote,
        encryptedMemory,
        encryptedInsideJoke,
        encryptedKey: null,
        fragmentKey: contentKey,
      };
    }

    /**
     * Standard Private Mode:
     * This gives recoverability but must be treated as sensitive.
     * In a stronger backend implementation, wrap this via KMS/Edge Function, not in browser.
     */
    const recoverySecret =
      params.cardRecoverySecret ||
      `${params.senderId}:${process.env.REACT_APP_LRNSY_PUBLIC_KEY_WRAP_PEPPER || "lrnsy-standard-v1"}`;

    const encryptedKeyPayload = await this.encryptText(contentKey, recoverySecret);

    return {
      privacyMode: params.privacyMode,
      encryptedMessage,
      encryptedPersonalNote,
      encryptedMemory,
      encryptedInsideJoke,
      encryptedKey: JSON.stringify(encryptedKeyPayload),
      fragmentKey: null,
    };
  }

  parseEncryptedValue(raw?: string | null): LrnsyEncryptedValue | null {
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as LrnsyEncryptedValue;
      if (!parsed.ciphertext || !parsed.iv || !parsed.salt || !parsed.version) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  buildUltraPrivateLink(baseUrl: string, fragmentKey: string): string {
    const separator = baseUrl.includes("#") ? "&" : "#";
    return `${baseUrl}${separator}key=${encodeURIComponent(fragmentKey)}`;
  }

  readFragmentKey(): string | null {
    if (typeof window === "undefined") return null;

    const fragment = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(fragment);
    return params.get("key");
  }

  createOpaqueToken(): string {
    return generateId().replaceAll("-", "") + generateId().replaceAll("-", "");
  }
}

export const lrnsyCryptoService = new LrnsyCryptoService();
